import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    Search, RefreshCw, FileText, User, Calendar, Check, Hourglass, Building, Link2
} from 'lucide-react';

const Prescriptions = () => {
    const [prescriptions, setPrescriptions] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Фильтры
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    
    // Фильтр по дате (По умолчанию - сегодняшний день)
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

    // Пагинация (Lazy Load)
    const [visibleCount, setVisibleCount] = useState(10);

    // Сброс пагинации при изменении фильтров
    useEffect(() => {
        setVisibleCount(10);
    }, [searchQuery, filterStatus, filterDate]);

    // Бесконечный скролл (Infinite Scroll)
    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
                setVisibleCount(prev => prev + 10);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/prescriptions');
            setPrescriptions(res.data);
        } catch (error) {
            console.error(error);
            alert("Ошибка загрузки данных");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Применение фильтров
    useEffect(() => {
        let result = prescriptions;
        
        // 1. Фильтр по статусу
        if (filterStatus !== 'all') {
            result = result.filter(p => p.status === filterStatus);
        }
        
        // 2. Фильтр по дате (если дата выбрана)
        if (filterDate) {
            result = result.filter(p => p.created_at && p.created_at.startsWith(filterDate));
        }

        // 3. Поиск по тексту
        if (searchQuery) {
            const lowerQ = searchQuery.toLowerCase();
            result = result.filter(p => 
                p.patient?.full_name?.toLowerCase().includes(lowerQ) ||
                p.doctor?.full_name?.toLowerCase().includes(lowerQ) ||
                p.medication?.toLowerCase().includes(lowerQ) ||
                p.patient?.medical_number?.toLowerCase().includes(lowerQ)
            );
        }
        
        setFilteredList(result);
    }, [searchQuery, filterStatus, filterDate, prescriptions]);

    const countActive = filteredList.filter(p => p.status === 'active').length;
    const countCompleted = filteredList.filter(p => p.status === 'completed').length;

    // Отрезаем список для Lazy Load
    const visiblePrescriptions = filteredList.slice(0, visibleCount);

    return (
        <div className="content-wrapper">
            <style>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                
                /* Стили для новых карточек рецептов */
                .presc-card {
                    background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 24px;
                    margin-bottom: 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); transition: box-shadow 0.2s;
                }
                .presc-card:hover { box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
                .p-icon-box {
                    width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
                }
                .p-icon-active { background: #ffedd5; color: #ea580c; }
                .p-icon-completed { background: #dcfce7; color: #16a34a; }
                
                .p-header-area { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                
                /* Бейджики статусов */
                .badge-presc-completed { background: #16a34a; color: white; padding: 4px 12px; border-radius: 6px; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-presc-active { background: #ea580c; color: white; padding: 4px 12px; border-radius: 6px; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                
                /* Сетка данных */
                .p-grid-info { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; border-top: 1px solid #f3f4f6; padding-top: 20px; }
                .p-col-title { font-size: 0.85rem; color: #6b7280; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
                .p-main-text { font-size: 0.95rem; font-weight: 600; color: #111827; margin: 0 0 4px 0; }
                .p-sub-text { font-size: 0.85rem; color: #9ca3af; margin: 0; display: flex; align-items: center; gap: 4px; }
            `}</style>

            {/* КАРТОЧКА УПРАВЛЕНИЯ */}
            <div className="card recipe-controls-card" style={{margin: '0'}}>
                <div className="card-header doc-header-flex">
                    <div className="doc-title-block">
                        <h4 className="card-title text-purple" style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <FileText size={24} color="#8b5cf6" /> Все рецепты системы
                        </h4>
                        <p className="card-subtitle">
                            Найдено: {filteredList.length} • Активных: {countActive} • Выданных: {countCompleted}
                        </p>
                    </div>
                    {/* Кнопка ОБНОВИТЬ вместо "Выписать рецепт" */}
                    <button className="flex-none btn-outline btn-sm" onClick={fetchData} style={{display:'flex', gap:'8px', alignItems:'center'}}>
                        <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} /> Обновить
                    </button>
                </div>

                <div className="card-body" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div className="recipe-search-wrapper" style={{ flex: '1 1 300px' }}>
                        <Search className="search-icon" size={18} />
                        <input 
                            type="text" 
                            className="recipe-search-input" 
                            placeholder="Поиск по пациенту, врачу, лекарству..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {/* НОВОЕ: Фильтр по дате */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f9fafb', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <Calendar size={16} color="#6b7280" />
                        <input 
                            type="date" 
                            style={{ border: 'none', background: 'transparent', outline: 'none', color: '#374151', fontSize: '0.9rem', cursor: 'pointer' }}
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                            title="Очистите поле, чтобы показать за все время"
                        />
                    </div>

                    <div className="recipe-filters">
                        <button className={`filter-pill ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>Все</button>
                        <button className={`filter-pill ${filterStatus === 'active' ? 'active' : ''}`} onClick={() => setFilterStatus('active')}>Активные</button>
                        <button className={`filter-pill ${filterStatus === 'completed' ? 'active' : ''}`} onClick={() => setFilterStatus('completed')}>Выданные</button>
                    </div>
                </div>
            </div>

            {/* СПИСОК РЕЦЕПТОВ */}
            <div>
                {isLoading ? (
                    <div style={{textAlign: 'center', padding: '40px', color: '#9ca3af'}}>Загрузка...</div>
                ) : visiblePrescriptions.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '40px', color: '#9ca3af'}}>Нет рецептов по вашему запросу или на выбранную дату</div>
                ) : (
                    visiblePrescriptions.map(p => {
                        const isCompleted = p.status === 'completed';
                        
                        const formatDateTime = (dateStr) => {
                            if (!dateStr) return 'Неизвестно';
                            const d = new Date(dateStr);
                            return `${d.toLocaleDateString('ru-RU')} ${d.toLocaleTimeString('ru-RU', {hour: '2-digit', minute:'2-digit'})}`;
                        };

                        const dateCreated = formatDateTime(p.created_at);
                        const dateCompleted = p.updated_at ? formatDateTime(p.updated_at) : dateCreated;
                        
                        // Собираем строку описания (Дозировка • Тип • Длительность)
                        const dosageStr = p.dosage || 'Не указано';
                        const typeStr = p.instructions || 'Не указано';
                        const durationStr = p.duration || 'Не указано';

                        return (
                            <div key={p.id} className="presc-card">
                                {/* ШАПКА КАРТОЧКИ */}
                                <div className="p-header-area">
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                        <div className={`p-icon-box ${isCompleted ? 'p-icon-completed' : 'p-icon-active'}`}>
                                            <FileText size={24} />
                                        </div>
                                        <div>
                                            <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#111827', margin: '0 0 4px 0' }}>
                                                {p.medication || 'Лекарство не указано'}
                                            </h3>
                                            <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                                                {dosageStr} • {typeStr} • {durationStr}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        {isCompleted ? (
                                            <span className="badge-presc-completed"><Check size={14}/> Выдан</span>
                                        ) : (
                                            <span className="badge-presc-active"><Hourglass size={14}/> Активен</span>
                                        )}
                                    </div>
                                </div>

                                {/* СЕТКА ДАННЫХ (3 КОЛОНКИ) */}
                                <div className="p-grid-info">
                                    {/* 1. Пациент */}
                                    <div>
                                        <div className="p-col-title"><User size={16}/> Пациент:</div>
                                        <p className="p-main-text">{p.patient?.full_name || 'Не указан'}</p>
                                        <p className="p-sub-text">Мед-№: {p.patient?.medical_number || `M-${p.patient_id}`}</p>
                                    </div>
                                    
                                    {/* 2. Врач */}
                                    <div>
                                        <div className="p-col-title"><User size={16}/> Врач:</div>
                                        <p className="p-main-text">{p.doctor?.full_name || 'Не указан'}</p>
                                        <p className="p-sub-text">
                                            <Building size={14}/> 
                                            {/* --- ИСПРАВЛЕНИЕ: Теперь больница подгружается --- */}
                                            {p.doctor?.hospital?.name || 'Больница не указана'}
                                        </p>
                                    </div>

                                    {/* 3. Даты */}
                                    <div>
                                        <div className="p-col-title"><Calendar size={16}/> Даты:</div>
                                        <p style={{ fontSize: '0.9rem', color: '#374151', margin: '0 0 6px 0' }}>
                                            Выписан: {dateCreated}
                                        </p>
                                        {isCompleted && (
                                            <>
                                                {/* --- ИСПРАВЛЕНИЕ: Выводим правильную дату выдачи --- */}
                                                <p style={{ fontSize: '0.9rem', color: '#374151', margin: '0 0 6px 0' }}>
                                                    Выдан: {dateCompleted}
                                                </p>
                                                {p.pharmacy && (
                                                    <p style={{ fontSize: '0.85rem', color: '#10b981', margin: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Link2 size={14}/> {p.pharmacy.organization_name || p.pharmacy.full_name || 'Аптека'}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* ИНДИКАТОР ПРОКРУТКИ */}
            {!isLoading && visiblePrescriptions.length < filteredList.length && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    Прокрутите вниз, чтобы загрузить еще...
                </div>
            )}
        </div>
    );
};

export default Prescriptions;